import {
  FLOW_LOADED,
  FLOW_CONTENT_COMPLETED,
  FLOW_QUESTION_COMPLETED,
  FLOW_INPUT_COMPLETED,
  FLOW_PAGE_COMPLETED,
  GOTO_NODE
} from 'src/config/actions';
import { NODE_ROUTE } from 'src/config/routes';
import { NODE_TYPES } from 'src/config/flowConfig';
import { PPFDispatcher } from 'src/dispatcher';
import router from 'src/router';
import appConfig from 'src/utils/appConfig';
import { Observer } from 'src/libs/observer';
import flowEventsHandler from 'src/utils/events/flowEventsHandler';
import BaseStore from './baseStore';
import RegistrationStore from './registrationStore';

export const STARTING_NODE_INDEX = 0;

class FlowStore extends BaseStore {
  constructor() {
    super();

    this.nodes = null;
    this.activeNodeIndex = -1;

    Object.values(NODE_TYPES).forEach(function(nodeType) {
      FlowStore.prototype[`is${nodeType}Active`] = function() {
        return (this.getActiveNode() && this.getActiveNode().getType() === nodeType);
      };
    });

    this.observer = new Observer(this, flowEventsHandler);
  }

  getActiveNodeId() {
    return this.getActiveNode().getNodeIdentifier();
  }

  getFlowLength() {
    if (this.nodes) {
      return this.nodes.length;
    }
    return 0;
  }

  getActiveNode() {
    if (this.activeNodeIndex < STARTING_NODE_INDEX || this.activeNodeIndex > this.getFlowLength()) {
      return null;
    }

    return this.nodes[this.activeNodeIndex];
  }

  getFlowLocales() {
    return this.locales;
  }

  getChangeEventPayload() {
    const node = this.getActiveNode();
    return {
      flow_length: this.getFlowLength(),
      node_id: node.id,
      node_type: node.getType(),
      node_ga_pageview_name: node.getGaPageViewName()
    };
  }

  updateRoute() {
    router.setPath(NODE_ROUTE, Object.assign({},
      appConfig.serialize(),
      { nodeId: this.activeNodeIndex }
    ));
  }

  pushDataLayer() {
    const {
      node_type: nodeType,
      node_ga_pageview_name: nodeGA
    } = this.getChangeEventPayload();
    const virtPath = document.location.href;
    const pageName = nodeGA || nodeType;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'pageview',
      virtPath,
      pageName, // as defined via node definitions
      marketingContentGroup: 'ppf', // as defined in src/initializer/setMetaTags
      flowID: appConfig.getFlowId()
    });
  }

  onDispatcherAction({ type, data }) {
    switch (type) {
      case FLOW_LOADED:
        this.locales = data.locales;
        this.nodes = data.nodes;
        this.activeNodeIndex = parseInt(data.nodeId, 10) || STARTING_NODE_INDEX;
        this.observer.observe();
        this.emitChange(this.getChangeEventPayload());
        this.pushDataLayer();
        break;

      case FLOW_CONTENT_COMPLETED:
        if (this.isContentNodeActive()) {
          this.activeNodeIndex++;
          this.emitChange(this.getChangeEventPayload());
          this.updateRoute();
          this.pushDataLayer();
        }
        break;

      case FLOW_QUESTION_COMPLETED:
        if (this.isQuestionNodeActive()) {
          this.activeNodeIndex++;
          this.emitChange(this.getChangeEventPayload());
          this.updateRoute();
          this.pushDataLayer();
        }
        break;

      case FLOW_INPUT_COMPLETED:
        if (this.isInputNodeActive()) {
          PPFDispatcher.waitFor([RegistrationStore.dispatchToken]);
          this.activeNodeIndex++;
          this.emitChange(this.getChangeEventPayload());
          this.updateRoute();
          this.pushDataLayer();
        }
        break;

      case FLOW_PAGE_COMPLETED:
        if (this.isPageNodeActive()) {
          this.activeNodeIndex++;
          this.emitChange(this.getChangeEventPayload());
          this.updateRoute();
          this.pushDataLayer();
        }
        break;

      case GOTO_NODE:
        this.activeNodeIndex = data.nodeIndex;
        this.emitChange(this.getChangeEventPayload());
        this.pushDataLayer();
        break;

      // no default
    }
  }
}

export default new FlowStore();
